/**
 * @file This file contains the data management logic of labels selector
 */
import _ from 'underscore';
import Promise from 'bluebird';
import $ from 'jquery';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { Selector } from '@bingads-webui/selector';
import { getEntitySet, isMCCLevel, getCurrentCustomerLabels } from './get-entity-set';

const selectorLoadAction = 'labelsselector/load?';

class RequestError extends Error {
  constructor(message, responseData) {
    super(message);
    this.responseData = responseData;
  }
}

// TODO: Remove the workaround after jsdata-odata is fixed
// and no longer translate rejections to a success promise
function requestErrorToRejection(result) {
  if (result &&
    ((result.status && result.status >= 300) ||
    (result.statusText && result.statusText === 'error'))) {
    throw new RequestError('Request failed', result);
  }
  return result;
}

export function hasCustomerLabel(ids) {
  if (!ids) {
    return false;
  }
  return ids.some((id) => {
    const parsed = JSON.parse(id);
    if (parsed && typeof parsed !== 'object') {
      return false;
    }
    return JSON.parse(id).Scope === 'Customer';
  });
}

function getIsCombinedScopeRequest(customerLevelLabelEnabled, scope, ids) {
  return (customerLevelLabelEnabled && !isMCCLevel(scope)) || hasCustomerLabel(ids);
}

function load(params, jsdata, edm, scope, isCombinedScopeRequest) {
  const result = [];
  const cacheResponse = false;
  const bypassCache = true;
  const headers = {
    'x-ms-refreshcampaigncache': true,
  };

  const currentScopeLabels = jsdata.Label
    .findAll(params, {
      cacheResponse,
      bypassCache,
      headers,
      entitySet: getEntitySet(scope, edm, $, edmCampaign),
    }).then(requestErrorToRejection);

  result.push(currentScopeLabels);

  if (isCombinedScopeRequest) {
    const customerLabels = jsdata.Label
      .findAll({ top: 0, ...params }, {
        cacheResponse,
        bypassCache,
        headers,
        entitySet: getCurrentCustomerLabels($, edmCampaign),
      }).then(requestErrorToRejection);
    result.push(customerLabels);
  }

  return result;
}

export function getLabelsResult(newData, isCombinedScopeRequest) {
  const accountLabels = newData[0];
  if (!isCombinedScopeRequest) {
    return accountLabels;
  }

  const mccLabels = newData[1];
  // Selector component shows a max of 50, 51 will trigger message indicating this.
  const maxLabelsToShow = 51;

  return accountLabels.concat(mccLabels).slice(0, maxLabelsToShow);
}

function getLabels({
  jsdata,
  itemsDisplayLimit,
  searchFilter,
  ids = [],
  cacheService,
  dataContextManager,
  edm,
  scope,
  customerLevelLabelEnabled,
}) {
  const params = {
    limit: itemsDisplayLimit + 1,
    filterOptions: {
      ignoreCase: true, // will take care of applying tolower to all textual
      // fields in the query and toLowerCase() to the values
    },
  };

  if (searchFilter) {
    params.where = {
      Name: {
        $contains: searchFilter,
      },
    };
  }

  if (ids.length > 0) {
    const idQuery = {
      $or: ids.map(id => ({
        Id: {
          $eq: (JSON.parse(id).Id ? JSON.parse(id).Id : id),
        },
      })),
    };
    const endQueries = [idQuery].concat(params.where || []);

    params.where = { $and: endQueries };
  }

  return new Promise((resolve) => {
    let cachedData;

    dataContextManager.getContext(selectorLoadAction, false, 'GET');
    const pseudoRequest = {
      url: selectorLoadAction,
      data: params,
      success: (data) => {
        _.each(data.labels, (label) => {
          if (label && typeof JSON.parse(label.Id) !== 'object' && label.Scope) {
            // eslint-disable-next-line no-param-reassign
            label.Id = JSON.stringify({ Id: label.Id, Scope: label.Scope });
          }
        });
        cachedData = data.labels;
      },
    };

    cacheService.cache(pseudoRequest);
    if (pseudoRequest.skipRequest) {
      resolve(cachedData);
    } else {
      const isCombinedScopeRequest =
        getIsCombinedScopeRequest(customerLevelLabelEnabled, scope, ids);
      const loadResult = load(params, jsdata, edm, scope, isCombinedScopeRequest);
      resolve(Promise.all(loadResult)
        .then((newData) => {
          const labels = getLabelsResult(newData, isCombinedScopeRequest);
          pseudoRequest.success({ labels });
          return labels;
        }));
    }
  });
}

/**
 * LabelsDataSource: provides labels fetch functions
 */
export class LabelsDataSource {
  /**
   * Initilize class settings
   *
   * @param {Object} param - An object containing these default options
   * @param {Object} param.jsdata - jsdata which will be used in labelsDatasource
   * @param {Object} param.dataContextManager - dataContextManager
   * @param {Function} param.dataContextManager.getContext - getContext for request
   * @param {Object} param.cacheService - cacheService
   * @param {Function} param.cacheService.cache - cache function
   * @returns {void} void
  */
  constructor({
    dataContextManager,
    cacheService,
    jsdata,
    scope,
    edm,
    customerLevelLabelEnabled,
  }) {
    this.options = {
      dataContextManager,
      cacheService,
      jsdata,
      scope,
      edm,
      customerLevelLabelEnabled,
    };
  }

  get itemsDisplayLimit() {
    return 50;
  }

  /**
   * get labels
   *
   * @param {String} viewFilter - view filter
   *  can specify to be selected or changed, or empty (means fetch everything)
   * @param {String} searchFilter - search filter
   * @param {Object} stats - selector status
   * @returns {Promise} result promise
  */
  get(viewFilter, searchFilter, stats) {
    let ids = this.lastIds || [];

    if (this.lastViewFilter !== viewFilter) {
      if (viewFilter === Selector.ViewFilter.selected) {
        ids = stats.checked.concat(stats.indeterminate).map(({ id }) => id);
      } else if (viewFilter === Selector.ViewFilter.changed) {
        ids = _.map(
          _.keys(_.extend({}, stats.changed, stats.added)),
          id => Number.parseInt(id, 10)
        );
      } else {
        ids = [];
      }
      this.lastIds = ids;
      this.lastViewFilter = viewFilter;
    }

    return getLabels(_.extend(
      {}, this.options,
      { itemsDisplayLimit: this.itemsDisplayLimit + 1, searchFilter, ids }
    ));
  }

  /**
   * get label by ids
   *
   * @param {Array[]} ids - ids to fetch, when empty, will return empty result
   * @returns {Promise} result promise
  */
  getByIds(ids) {
    if (ids && ids.length > 0) {
      return getLabels(_.extend(
        {}, this.options,
        { itemsDisplayLimit: this.itemsDisplayLimit + 1, searchFilter: undefined, ids }
      ));
    }

    return Promise.resolve([]);
  }
}
